// biome-ignore lint/style/useNodejsImportProtocol: <explanation>
import crypto from 'crypto';
import { createId } from '@paralleldrive/cuid2';

import { SongPartText, nonLyricsTypes } from '@/constant';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Chord, Interval } from 'tonal/dist';

import { formatDate } from 'date-fns';
import { HTTPException } from 'hono/http-exception';
import { isValidChords } from './chord';

type SongPartType = 'i' | 'v' | 'p' | 'c' | 'b' | 't' | 'I' | 'e';
export interface SongPart {
	type: string;
	times: number;
	lines: string[];
	result?: SongPartResult[];
	name?: string;
	isHideChord?: boolean;
}

type SongPartResult = {
	chords: string[];
	lyrics: string;
};

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

function getChordAndLyrics(lines: string[], partType: SongPartType) {
	if (nonLyricsTypes.includes(partType)) {
		return lines
			.map((line) => {
				const chords = line.split(' ');
				const isValid = isValidChords(chords);
				if (!isValid) {
					return {
						chords: [],
						lyrics: ''
					};
				}

				return {
					chords,
					lyrics: ''
				};
			})
			.filter((x) => x.chords.length > 0);
	}

	let isChord = true;
	let chords: string[] = [];

	const result = [];

	for (const line of lines) {
		if (isChord) {
			const _chords = line.split(' ');
			const isValid = isValidChords(_chords);
			if (!isValid) {
				continue;
			}

			chords.push(..._chords);
			isChord = false;

			continue;
		}

		const _chords = line.split(' ');
		const isValid = isValidChords(_chords);
		if (isValid) {
			continue;
		}

		if (chords.length > 0 && line.trim() !== '') {
			result.push({
				chords,
				lyrics: line
			});
		}

		chords = [];
		isChord = true;
	}

	return result;
}

export function splitSongText(songText: string): SongPart[] {
	if (!songText) {
		return [];
	}

	const parts: SongPart[] = [];
	let currentPart: SongPart | null = null;

	const countParts = {
		i: 0,
		v: 0,
		p: 0,
		c: 0,
		b: 0,
		t: 0,
		I: 0,
		e: 0
	};

	const lines = songText.trim().split('\n');
	for (const line of lines) {
		const trimmedLine = line.trim().replace(/\s+/g, ' ');

		if (trimmedLine === '') {
			continue;
		}

		if (trimmedLine.startsWith('#')) {
			const partType = trimmedLine.slice(1);

			if (currentPart !== null) {
				currentPart.times = ++countParts[currentPart.type as SongPartType];
				currentPart.result = getChordAndLyrics(currentPart.lines, currentPart.type as SongPartType);

				parts.push(currentPart);
			}

			// Create a new part object
			currentPart = {
				times: 0,
				type: partType,
				lines: []
			};

			continue;
		}

		if (currentPart !== null) {
			currentPart.lines.push(trimmedLine);
		}
	}

	if (currentPart !== null) {
		currentPart.times = ++countParts[currentPart.type as SongPartType];
		currentPart.result = getChordAndLyrics(currentPart.lines, currentPart.type as SongPartType);

		parts.push(currentPart);
	}

	return parts
		.filter((part) => part.times > 0)
		.map((part) => {
			const times = countParts[part.type as SongPartType];
			if (times > 1) {
				part.name = `${SongPartText[part.type]} ${part.times}`;
			} else {
				part.name = SongPartText[part.type];
			}

			return part;
		});
}

export function getLyricsFromSongText(songText: string): string {
	if (!songText) {
		return '';
	}

	const result: string[] = [];

	const lines = songText.trim().split('\n');
	for (const line of lines) {
		const trimmedLine = line.trim().replace(/\s+/g, ' ');

		if (trimmedLine === '') {
			continue;
		}

		if (trimmedLine.startsWith('#')) {
			continue;
		}

		if (!trimmedLine.includes('_')) {
			continue;
		}

		result.push(trimmedLine.replace(/_/g, '').trim().replace(/\s+/g, ' '));
	}

	return result.join(' ').replace(/Jê\s+sus/g, 'Jêsus');
}

export const getChordsAndLyricsText = (chords: string[], lyrics: string, semitones = 0) => {
	if (!chords.length) {
		return {
			chordsText: '',
			lyricsText: lyrics
		};
	}

	if (!lyrics) {
		return {
			chordsText: chords.join(' '),
			lyricsText: ''
		};
	}

	let chordLine = '';
	let lyricsLine = '';
	let k = 0;
	const chordFirst = 0;

	if (lyrics.indexOf('_ ') === 0) {
		// lyricsLine += ' ';
	}

	lyrics.split('').forEach((x, index) => {
		if (x !== '_') {
			lyricsLine += x;
			return;
		}

		const chord = chords[k++];

		chordLine = chordLine.padEnd(index - k, '-') + transposeChord(chord, semitones);
	});

	return {
		chordsText: chordLine,
		lyricsText: lyricsLine
	};
};

export const transposeChord = (chord: string, semitones: number) => {
	if (!chord) {
		return '';
	}

	if (chord.includes('/')) {
		const [root, bass] = chord.split('/');

		return `${Chord.transpose(root, Interval.fromSemitones(semitones))}/${Chord.transpose(bass, Interval.fromSemitones(semitones))}`;
	}

	return Chord.transpose(chord, Interval.fromSemitones(semitones));
};

export function isValidURL(url: string) {
	if (!url) {
		return false;
	}

	return !!url.match(
		/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
	);
}

export function getCapitalizedText(text: string | null | undefined) {
	if (!text) {
		return '';
	}

	const ignoreWords: Record<string, boolean> = {
		TVCHH: true
	};

	const trimText = text?.trim()?.replace(/\s+/g, ' ');

	if (trimText && ignoreWords[trimText]) {
		return trimText;
	}

	return (
		text
			?.trim()
			?.replace(/\s+/g, ' ')
			?.toLowerCase()
			?.split(' ')
			.map((s: string) => s[0].toUpperCase() + s.slice(1))
			.join(' ') || ''
	);
}

export function splitWordForPreview(word: string | null | undefined) {
	if (!word) {
		return [];
	}

	return word.trim().replace(/\s+/g, ' ').split(' ');
}

export function getFullTextSearch(text: string) {
	const words = text.trim().replace(/\s+/g, ' ').replace(/,\.-/gi, '').split(' ');

	return `%${words.join(' ')}%`;
}

export function getNext6Months() {
	const now = new Date();

	const months = [];

	for (let i = 0; i < 6; i++) {
		const newDate = new Date(now.getFullYear(), now.getMonth() + i, 1);

		months.push(formatDate(newDate, 'MM/yyyy'));
	}

	return months;
}

export function safeStringify(obj: unknown) {
	const seen = new Set();

	const result = JSON.stringify(obj, (_, value) => {
		if (typeof value === 'object' && value !== null) {
			// Duplicate reference found, discard key
			if (seen.has(value)) return;

			// Store value in our collection
			seen.add(value);
		}
		return value;
	});

	return result;
}

export function getYoutubeThumbnail(url: string) {
	if (!url) {
		return;
	}

	const regex = /(?:youtu\.be\/|youtube\.com\/(?:.*v=|.*\/))([a-zA-Z0-9_-]{11})/;

	const match = url.match(regex);

	return match ? `https://img.youtube.com/vi/${match[1]}/0.jpg` : undefined;
}

export function getHashKey(prefix: string, params: Record<string, unknown>) {
	let key = '';
	if (params) {
		const text = JSON.stringify(params);
		key = crypto.createHash('sha256').update(text).digest('hex');
	}
	return `${prefix}${key}`;
}

export function throwHttpException(error: unknown) {
	if (error instanceof HTTPException) {
		throw error;
	}

	throw new HTTPException(500, { cause: error });
}

export function safeParseJSON(str: unknown) {
	const json = typeof str === 'string' ? str : JSON.stringify(str || {});

	if (!json.startsWith('{')) {
		return {};
	}

	return JSON.parse(json);
}

export function cuid() {
	return createId();
}
